const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

function pad2(value) {
  return String(value).padStart(2, '0');
}

function finiteAmount(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

export function calendarDateKey(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'string') {
    const match = value.trim().match(DATE_KEY_RE);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return [
    date.getFullYear(),
    pad2(date.getMonth() + 1),
    pad2(date.getDate()),
  ].join('-');
}

export function financeBelongsToPersonalTask(finance, tasks = []) {
  if (!finance) return false;
  if (finance.task_is_personal === true || finance.is_personal === true) {
    return true;
  }

  const taskId = finance.task_id;
  if (taskId == null || taskId === '') return false;

  const task = (tasks || []).find((item) => (
    String(item?.server_id ?? item?.id ?? item?.temp_id) === String(taskId)
  ));

  return task?.is_personal === true;
}

export function visibleFinances(finances = [], tasks = []) {
  return (finances || []).filter((finance) => (
    finance
    && ['income', 'expense'].includes(finance.type)
    && !financeBelongsToPersonalTask(finance, tasks)
  ));
}

export function financePeriodKey(value, group = 'month') {
  const key = calendarDateKey(value);
  if (!key) return null;

  if (group === 'day') return key;
  if (group === 'month') return key.slice(0, 7);

  if (group === 'week') {
    const [year, month, day] = key.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    const weekDay = date.getUTCDay();
    date.setUTCDate(date.getUTCDate() - (weekDay === 0 ? 6 : weekDay - 1));
    return [
      date.getUTCFullYear(),
      pad2(date.getUTCMonth() + 1),
      pad2(date.getUTCDate()),
    ].join('-');
  }

  return key.slice(0, 7);
}

export function buildFinanceStats(finances = [], tasks = [], taxRate = 7) {
  const visible = visibleFinances(finances, tasks);
  const income = visible
    .filter((finance) => finance.type === 'income')
    .reduce((sum, finance) => sum + finiteAmount(finance.amount), 0);
  const expense = visible
    .filter((finance) => finance.type === 'expense')
    .reduce((sum, finance) => sum + finiteAmount(finance.amount), 0);
  const normalizedTaxRate = Number.isFinite(Number(taxRate)) ? Number(taxRate) : 7;
  const taxAmount = Math.round((income * normalizedTaxRate / 100) * 100) / 100;

  return {
    income,
    expense,
    balance: income - expense,
    tax_rate: normalizedTaxRate,
    tax_amount: taxAmount,
    income_after_tax: income - taxAmount,
    net_after_tax: income - taxAmount - expense,
    fromCache: true,
  };
}

export function buildFinancePeriods(finances = [], tasks = [], group = 'month', taxRate = 7) {
  const normalizedGroup = ['day', 'week', 'month'].includes(group) ? group : 'month';
  const normalizedTaxRate = Number.isFinite(Number(taxRate)) ? Number(taxRate) : 7;
  const byPeriod = {};

  visibleFinances(finances, tasks).forEach((finance) => {
    const period = financePeriodKey(finance.date || finance.created_at, normalizedGroup);
    if (!period) return;

    if (!byPeriod[period]) {
      byPeriod[period] = { period, income: 0, expense: 0 };
    }

    byPeriod[period][finance.type] += finiteAmount(finance.amount);
  });

  return Object.values(byPeriod)
    .map((row) => {
      const taxAmount = Math.round((row.income * normalizedTaxRate / 100) * 100) / 100;
      return {
        ...row,
        profit: row.income - row.expense,
        tax_rate: normalizedTaxRate,
        tax_amount: taxAmount,
        income_after_tax: row.income - taxAmount,
        net_after_tax: row.income - taxAmount - row.expense,
      };
    })
    .sort((a, b) => b.period.localeCompare(a.period));
}

export function getPeriodRange(period, group = 'month') {
  if (group === 'day') {
    return { start_date: period, end_date: period };
  }

  if (group === 'month') {
    const match = String(period || '').match(/^(\d{4})-(\d{2})$/);
    if (!match) return {};

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) return {};

    const endDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return {
      start_date: `${year}-${pad2(month)}-01`,
      end_date: `${year}-${pad2(month)}-${pad2(endDay)}`,
    };
  }

  if (group === 'week') {
    const startKey = calendarDateKey(period);
    if (!startKey) return {};

    const [year, month, day] = startKey.split('-').map(Number);
    const end = new Date(Date.UTC(year, month - 1, day + 6));
    return {
      start_date: startKey,
      end_date: [
        end.getUTCFullYear(),
        pad2(end.getUTCMonth() + 1),
        pad2(end.getUTCDate()),
      ].join('-'),
    };
  }

  return {};
}

export function currentMonthKey(now = new Date()) {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
}
